import { ref } from 'vue'
import type { PackId } from '@shared/index'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'
import { useWalletStore } from '../stores/wallet'

/** Confirmed bug reports still waiting to be explained. 0 hides the toast. */
export const bugRewardNoticeCount = ref(0)

let inflight: Promise<void> | null = null
let claimed = false
let acked = false

/**
 * Loads unread shop packs from confirmed bugs and arms the toast.
 * A miss stays unclaimed so a later visit can try again.
 */
export function pullBugRewardNotice(): Promise<void> {
  if (claimed) return Promise.resolve()
  if (inflight) return inflight
  inflight = loadNotice().finally(() => {
    inflight = null
  })
  return inflight
}

async function loadNotice(): Promise<void> {
  const token = useAuthStore().token
  if (!token) return
  const collection = useCollectionStore()
  const epochAtFetch = collection.mutationEpoch()
  try {
    const body = await api<{ count: number; packs: Record<PackId, number>; coins?: number }>(
      '/account/bug-reward-notice',
      { token },
    )
    if (!body.count) return
    if (collection.mutationEpoch() !== epochAtFetch) return
    claimed = true
    acked = false
    collection.applyPacks(body.packs)
    if (typeof body.coins === 'number') useWalletStore().setCoins(body.coins)
    bugRewardNoticeCount.value = body.count
  } catch {
    /* A later visit can try again. */
  }
}

/** Marks the notice read. Safe to call more than once. */
export async function ackBugRewardNotice(): Promise<void> {
  if (acked) return
  acked = true
  const token = useAuthStore().token
  if (!token) return
  try {
    await api('/account/bug-reward-notice/ack', { method: 'POST', token })
  } catch {
    acked = false
  }
}
